/**
 * Fachliches Modell einer Wohnungsübergabe.
 *
 * Bewusst ohne UI- oder Speicher-Details: dieselben Typen tragen den Rundgang
 * im Browser, die spätere Offline-Ablage und den PDF-Export.
 */

/** Eine Übergabe ist entweder ein Einzug oder ein Auszug. */
export type UebergabeArt = 'einzug' | 'auszug';

/** Zustand eines Raums oder Gegenstands, wie er im Protokoll steht. */
export type Zustand = 'in_ordnung' | 'gebrauchsspuren' | 'mangel';

/** Ein einzelner Befund in einem Raum. */
export interface Mangel {
	id: string;
	/** Was ist betroffen — „Fenstergriff", „Decke", „Laminat". */
	gegenstand: string;
	/** Der Befund selbst — „locker", „Wasserfleck", „tiefe Kratzer". */
	beschreibung: string;
	zustand: Zustand;
	/** IDs der Fotos, die diesen Befund belegen. */
	fotoIds: string[];
	/**
	 * Der Originalsatz, aus dem dieser Eintrag entstanden ist.
	 * Bleibt erhalten, damit im Zweifel nachvollziehbar ist, was diktiert wurde.
	 */
	quelle?: string;
}

export interface Raum {
	id: string;
	name: string;
	zustand: Zustand;
	maengel: Mangel[];
	fotoIds: string[];
	/** Freitext, der sich keinem einzelnen Mangel zuordnen lässt. */
	notiz?: string;
}

/** Zählerstand. Der Fototyp ist wichtig: das Foto ist der eigentliche Beleg. */
export interface Zaehler {
	id: string;
	art: 'strom' | 'gas' | 'wasser_kalt' | 'wasser_warm' | 'heizung' | 'sonstige';
	bezeichnung?: string;
	nummer?: string;
	stand: string;
	fotoIds: string[];
}

export interface Schluessel {
	id: string;
	art: string;
	anzahl: number;
}

/** Person, die unterschreibt. */
export interface Beteiligter {
	id: string;
	name: string;
	rolle: 'uebergeber' | 'uebernehmer' | 'zeuge';
	/** Unterschrift als Data-URL, erst beim Abschluss gesetzt. */
	unterschrift?: string;
}

export interface Foto {
	id: string;
	/** Data-URL oder lokaler Blob-Verweis — offline-fähig, bis synchronisiert wird. */
	datenUrl: string;
	aufgenommenAm: string;
}

/** Adresse, unter der mehrere Übergaben (Einzug, Auszug, ...) gesammelt werden. */
export interface Objekt {
	id: string;
	strasse: string;
	plz: string;
	ort: string;
	lage?: string;
	erstelltAm: string;
}

export type ObjektAdresse = Omit<Objekt, 'id' | 'erstelltAm'>;

export function neuesObjekt(adresse: ObjektAdresse): Objekt {
	return { id: crypto.randomUUID(), ...adresse, erstelltAm: new Date().toISOString() };
}

export interface Protokoll {
	id: string;
	art: UebergabeArt;
	/**
	 * Welchem Objekt diese Übergabe zugeordnet ist — verknüpft Einzug und
	 * Auszug derselben Wohnung für die Objekt-Historie und den
	 * Vorher-Nachher-Vergleich. Fehlt bei ganz alten, vor der
	 * Objektverwaltung erstellten Protokollen.
	 */
	objektId?: string;
	/**
	 * Anschrift der Wohnung, wie sie ins PDF kommt — bewusst als eigene
	 * Momentaufnahme statt eines Lookups über `objektId`, damit ein
	 * einmal erstelltes Dokument nie durch eine spätere Adressänderung
	 * am Objekt rückwirkend anders aussieht.
	 */
	objekt: {
		strasse: string;
		plz: string;
		ort: string;
		lage?: string;
	};
	beteiligte: Beteiligter[];
	raeume: Raum[];
	zaehler: Zaehler[];
	schluessel: Schluessel[];
	fotos: Foto[];
	erstelltAm: string;
	abgeschlossenAm?: string;
}

/** Leeres Protokoll als Startpunkt eines Rundgangs — optional an ein bestehendes Objekt angehängt. */
export function neuesProtokoll(art: UebergabeArt, objekt?: Objekt): Protokoll {
	return {
		id: crypto.randomUUID(),
		art,
		objektId: objekt?.id,
		objekt: { strasse: objekt?.strasse ?? '', plz: objekt?.plz ?? '', ort: objekt?.ort ?? '', lage: objekt?.lage },
		beteiligte: [],
		raeume: [],
		zaehler: [],
		schluessel: [],
		fotos: [],
		erstelltAm: new Date().toISOString()
	};
}

/** Räume, mit denen die meisten Übergaben anfangen. Frei erweiterbar. */
export const RAUM_VORSCHLAEGE = [
	'Flur',
	'Wohnzimmer',
	'Schlafzimmer',
	'Küche',
	'Bad',
	'Gäste-WC',
	'Kinderzimmer',
	'Abstellraum',
	'Balkon',
	'Keller'
] as const;

/** Menschenlesbare Bezeichnung je Zustand — genutzt von Rundgang.tsx und Abschluss.tsx. */
export const ZUSTAND_LABEL: Record<Zustand, string> = {
	in_ordnung: 'In Ordnung',
	gebrauchsspuren: 'Gebrauchsspuren',
	mangel: 'Mangel'
};

/** Menschenlesbare Bezeichnung je Zähler-Art — an einer Stelle, damit Zaehler.tsx und Abschluss.tsx nicht auseinanderlaufen. */
export const ZAEHLER_ART_LABEL: Record<Zaehler['art'], string> = {
	strom: 'Strom',
	gas: 'Gas',
	wasser_kalt: 'Wasser kalt',
	wasser_warm: 'Wasser warm',
	heizung: 'Heizung',
	sonstige: 'Sonstige'
};

export const ZAEHLER_ARTEN = Object.entries(ZAEHLER_ART_LABEL).map(([art, label]) => ({
	art: art as Zaehler['art'],
	label
}));

/** Menschenlesbare Bezeichnung je Rolle — genutzt von Unterschriften.tsx und Abschluss.tsx. */
export const BETEILIGTER_ROLLE_LABEL: Record<Beteiligter['rolle'], string> = {
	uebergeber: 'Übergeber',
	uebernehmer: 'Übernehmer',
	zeuge: 'Zeuge'
};

export const BETEILIGTER_ROLLEN = Object.entries(BETEILIGTER_ROLLE_LABEL).map(([rolle, label]) => ({
	rolle: rolle as Beteiligter['rolle'],
	label
}));
